// DOM-free so it can be tested in the worker program; the page imports it
// through ./browser.

/**
 * What `/auth/token` said: a token, "no session" (the worker's 401), or
 * that it could not be asked (network failure, timeout, 5xx, a body that
 * is not a token). Only the second is a sign-out.
 */
export type TokenResult =
  | { kind: "token"; token: string }
  | { kind: "signed-out" }
  | { kind: "unreachable" };

export const TOKEN_FETCH_TIMEOUT_MS = 10_000;

export type TokenFetch = (
  input: string,
  init: RequestInit,
) => Promise<Response>;

const DEFAULT_TOKEN_PATH = "/auth/token";

export async function fetchTokenResult(
  fetcher: TokenFetch = (input, init) => fetch(input, init),
  path: string = DEFAULT_TOKEN_PATH,
): Promise<TokenResult> {
  // AbortController, not AbortSignal.timeout: that is Safari 16+, below
  // the reader's browser floor, and would make every ask "unreachable".
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TOKEN_FETCH_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetcher(path, {
      credentials: "same-origin",
      signal: controller.signal,
    });
  } catch {
    return { kind: "unreachable" };
  } finally {
    clearTimeout(timer);
  }
  if (response.status === 401) return { kind: "signed-out" };
  if (!response.ok) return { kind: "unreachable" };
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "token" in body &&
      typeof body.token === "string"
    ) {
      return { kind: "token", token: body.token };
    }
  } catch {
    // An unparseable body is a broken answer, not a sign-out.
  }
  return { kind: "unreachable" };
}
